import { addDoc, collection, doc, getDoc, getDocs, getFirestore, orderBy, query, setDoc, where } from "firebase/firestore";
import { create } from "zustand";
import { firebaseApp } from "../config";
import dayjs from "dayjs";
import { EventTypes, IEvent } from "../models/events";
import { notifications } from "@mantine/notifications";
import { IPatient } from "../models/patient";
import { patientKey } from "../utilities/patient-key";
import { startCase } from "../utilities/title";

interface IUserStore {
    accessToken: string | null;
    uid: string | null;
    events: IEvent[];
    clearUserInfo: () => void;
    getGeneralEvents: () => void;
    getBehaviorEvents: () => void;
    setUIDAndToken: (uid: string, token: string) => void;
    addPatient: (eventId: string, patient: IPatient) => Promise<boolean>;
}

export const useUserStore = create<IUserStore>((set) => ({
    accessToken: localStorage.getItem('accessToken'),
    uid: localStorage.getItem('defaultOrgUID'),
    events: [],
    clearUserInfo: () => {
        localStorage.removeItem('uid');
        localStorage.removeItem('accessToken');
        set((state: IUserStore) => ({ ...state, uid: null, accessToken: null }));
    },
    getGeneralEvents: async () => {
        const db = getFirestore(firebaseApp);
        const eventsCollection = collection(db, 'events');
        const eventsDocs = await getDocs(query(eventsCollection, orderBy("date"), where('date', '>=', dayjs().add(2, 'days').toDate())));
        let events = eventsDocs.docs.map((doc) => ({ type: doc.get('type'), limit: doc.get('limit'), uid: doc.id, patientTotal: doc.get('patientTotal') ?? 0, date: doc.get('date').toDate().toISOString() }));
        events = events.filter((event) => event.type === EventTypes.GENERAL);
        events = events.filter((event) => event.limit > event.patientTotal);
        set((state: IUserStore) => ({ ...state, events }));
    },
    getBehaviorEvents: async () => {
        const db = getFirestore(firebaseApp);
        const eventsCollection = collection(db, 'events');
        const eventsDocs = await getDocs(query(eventsCollection, orderBy("date"), where('date', '>=', dayjs().add(1, 'days').toDate())));
        let events = eventsDocs.docs.map((doc) => ({ type: doc.get('type'), limit: doc.get('limit'), uid: doc.id, patientTotal: doc.get('patientTotal') ?? 0, date: doc.get('date').toDate().toISOString() }));
        events = events.filter((event) => event.type === EventTypes.BEHAVIOR);
        console.log(events);
        events = events.filter((event) => event.limit > event.patientTotal);
        set((state: IUserStore) => ({ ...state, events }));
    },
    setUIDAndToken: (uid: string, token: string) => {
        localStorage.setItem('uid', uid);
        localStorage.setItem('accessToken', token);
        set((state: IUserStore) => ({ ...state, uid, accessToken: token }));
    },
    
    addPatient: async (eventId: string, patient: IPatient): Promise<boolean> => {
        try {
            const eventDoc = doc(collection(getFirestore(firebaseApp), 'events'), eventId);
            const eventDocData = await getDoc(eventDoc);
    
            const patientCollection = collection(eventDoc, 'patients');
    
            const patientDoc = doc(collection(getFirestore(firebaseApp), 'patients'), patientKey({
                firstName: patient.firstName,
                lastName: patient.lastName,
                phone: patient.phone,
                dob: patient.dob,
            }));
            const patientDocData = await getDoc(patientDoc);
            let createPatientUID: string;
            if (!patientDocData.exists()) {
                await setDoc(patientDoc, {
                    firstName: startCase(patient.firstName),
                    lastName: startCase(patient.lastName),
                    phone: patient.phone,
                    email: patient.email,
                    dob: new Date(patient.dob),
                    events: [eventId],
                    address: patient.address ?? '',
                    city: patient.city ? startCase(patient.city) : '',
                    zipCode: patient.zipCode ?? '',
                    latestEventId: eventId ?? '',
                    officeStaffOnly: patient.officeStaffOnly ?? false,
                    isNewPatient: patient.isNewPatient ?? 'no',
                });
                const result = await addDoc(patientCollection, { ...patient, 
                    lastEventId: eventId,
                    events: [eventId],
                    firstName: startCase(patient.firstName), lastName: startCase(patient.lastName),
                    dob: new Date(patient.dob),
                });
                createPatientUID = result.id;
            } else {
                if (patientDocData.get('events').includes(eventId)) {
                    notifications.show({ title: 'Error', message: 'Already exists in this event', color: 'red' });
                    return false;
                }
                await setDoc(patientDoc, {
                    latestEventId: eventId,
                    events: [...patientDocData.get('events'), eventId],
                }, { merge: true});
                const result = await addDoc(patientCollection, { ...patient, 
                    lastEventId: eventId,
                    events: [...patientDocData.get("events"), eventId],
                    firstName: startCase(patient.firstName), 
                    lastName: startCase(patient.lastName), 
                    dob: new Date(patient.dob),
                });
                createPatientUID = result.id;
            }
            await setDoc(eventDoc, {
                patientTotal: (eventDocData.get('patientTotal') ?? 0) + 1,
            }, { merge: true });
    
            // Trigger sms message
            const result = await fetch(`${import.meta.env.VITE_MAIN_API}/api/v1/send-message/${eventId}/${createPatientUID}/`);
            if (!result.ok) {
                notifications.show({ title: 'Error', message: 'An error occurred while sending SMS', color: 'red' });
            } else {
                notifications.show({ title: 'Success', message: 'SMS sent successfully', color: 'green' });
            }
            notifications.show({ title: 'Success', message: 'Added successfully', color: 'green' });
            return true;
        } catch (error: unknown) {
            console.error(error);
            notifications.show({ title: 'Error', message: 'An error occurred while submitting request', color: 'red' });
            return false;
        }
    },
 }));